import { create } from "zustand";
import { getCookie, setCookie, deleteCookie } from "@/lib/cookies";
import { getFromLocalStorage } from "@/lib/apiClient";
import { recordWorkspaceAccess } from "@/lib/recordWorkspaceAccess";
import { addPersistScopeId, clearPersistedQueryCache } from "@/lib/queryPersister";

// Check localStorage for workspace_id as a fallback (migration from old code)
const getInitialWorkspaceId = () => {
  const cookieWorkspaceId = getCookie("workspace_id");
  if (cookieWorkspaceId) return cookieWorkspaceId;

  // Try to get from localStorage and migrate to cookie
  const localStorageWorkspaceId = getFromLocalStorage("workspace_id");
  if (localStorageWorkspaceId) {
    setCookie("workspace_id", localStorageWorkspaceId);
    return localStorageWorkspaceId;
  }

  return undefined;
};

// Get initial superadmin flag from cookie
const getInitialIsSuperadmin = (): boolean => {
  const cookie = getCookie("is_superadmin");
  return cookie === "true";
};

interface UserInfo {
  email: string;
  firstName?: string;
  lastName?: string;
  avatar_id?: number | null;
  profile_image_url?: string | null;
}

// The active workspace is the only source of truth for tenant context. The
// owning account is always derived from the workspace (workspace.account), never
// stored here — a separate "active account" value drifts out of sync (it used to
// show the wrong account in Settings). Don't reintroduce one.
export const useWorkspaceStore = create<{
  workspaceId: string | undefined;
  currentWorkspace: { id: string } | null;
  userInfo: UserInfo | null;
  isSuperadmin: boolean;
  onboardingRequired: boolean;
  setWorkspaceId: (workspaceId: string) => void;
  setUserInfo: (userInfo: UserInfo) => void;
  setIsSuperadmin: (isSuperadmin: boolean) => void;
  setOnboardingRequired: (required: boolean) => void;
  refreshWorkspaceId: () => void;
  clearWorkspaceId: () => void;
  clearUserInfo: () => void;
}>((set) => ({
  workspaceId: getInitialWorkspaceId(),
  currentWorkspace: getInitialWorkspaceId() ? { id: getInitialWorkspaceId()! } : null,
  userInfo: (() => {
    const userCookie = getCookie("user_info");
    return userCookie ? JSON.parse(userCookie) : null;
  })(),
  isSuperadmin: getInitialIsSuperadmin(),
  onboardingRequired: false,
  setWorkspaceId: (workspaceId: string) => {
    const previousWorkspaceId = getCookie("workspace_id");
    setCookie("workspace_id", workspaceId);
    set({ workspaceId, currentWorkspace: { id: workspaceId } });
    // Record the access for the staff console's login visibility (best-effort).
    recordWorkspaceAccess(workspaceId);
    // Perf plan §4.6 — every tenant switch (user-initiated switch, superadmin
    // "view as" adoption, or a fresh login into a different workspace than
    // this browser profile last held) wipes the persisted query cache. Every
    // persisted key is workspace-scoped already, so this isn't fixing a
    // correctness bug — it's hygiene against a browser profile that hops
    // between accounts/workspaces accumulating unbounded stale IndexedDB
    // entries for tenants the user isn't looking at anymore.
    if (previousWorkspaceId && previousWorkspaceId !== workspaceId) {
      clearPersistedQueryCache();
      addPersistScopeId(workspaceId);
    }
  },
  setUserInfo: (userInfo: UserInfo) => {
    setCookie("user_info", JSON.stringify(userInfo));
    set({ userInfo });
  },
  setIsSuperadmin: (isSuperadmin: boolean) => {
    setCookie("is_superadmin", String(isSuperadmin));
    set({ isSuperadmin });
  },
  setOnboardingRequired: (required: boolean) => set({ onboardingRequired: required }),
  refreshWorkspaceId: () => {
    const workspaceId = getCookie("workspace_id");
    set({
      workspaceId: workspaceId || undefined,
      currentWorkspace: workspaceId ? { id: workspaceId } : null
    });
  },
  clearWorkspaceId: () => {
    setCookie("workspace_id", "", -1); // Delete cookie
    set({ workspaceId: undefined, currentWorkspace: null });
  },
  clearUserInfo: () => {
    deleteCookie("user_info");
    deleteCookie("is_superadmin");
    // Clear legacy cookies too so stale values from older clients don't
    // linger (the store no longer owns active-account or role state).
    deleteCookie("account_info");
    deleteCookie("account_id");
    deleteCookie("user_roles");
    set({ userInfo: null, isSuperadmin: false });
  },
}));
